import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Target, Zap, Heart, Users, Shield, RefreshCw, TrendingUp, CheckCircle, AlertCircle, ExternalLink } from "lucide-react";

const Index = () => {
  const therapyApproaches = [
    {
      icon: <RefreshCw className="w-8 h-8" />,
      title: "Dislocation Therapy",
      description: "Disrupt negative patterns and create space for positive behavioral changes through strategic pattern interruption.",
      image: "https://images.unsplash.com/photo-1662493724051-01a5046b530b?w=400&auto=format&fit=crop&q=80",
      techniques: ["Pattern interruption", "Environmental changes", "Routine disruption", "Cognitive reframing"]
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Motivation Building",
      description: "Develop sustainable motivation through goal-setting, reward systems, and intrinsic motivation cultivation.",
      image: "https://images.unsplash.com/photo-1758691737138-7b9b1884b1db?w=400&auto=format&fit=crop&q=80",
      techniques: ["SMART goals", "Reward systems", "Progress tracking", "Values alignment"]
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Addiction Recovery",
      description: "Evidence-based approaches to overcome addictive behaviors and build lasting recovery foundations.",
      image: "https://images.unsplash.com/photo-1586974175094-0a7259238613?w=400&auto=format&fit=crop&q=80",
      techniques: ["Cognitive behavioral therapy", "Mindfulness practices", "Support networks", "Relapse prevention"]
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Emotional Regulation",
      description: "Learn to manage emotions effectively and develop healthy coping mechanisms for stress and triggers.",
      image: "https://images.unsplash.com/photo-1758607234692-51ac051a2a4d?w=400&auto=format&fit=crop&q=80",
      techniques: ["Breathing techniques", "Emotional awareness", "Stress management", "Trigger identification"]
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Support Systems",
      description: "Build strong support networks and therapeutic relationships essential for sustained recovery and growth.",
      image: "https://images.unsplash.com/photo-1626783658527-d7355850e35d?w=400&auto=format&fit=crop&q=80",
      techniques: ["Group therapy", "Peer support", "Family involvement", "Professional guidance"]
    }
  ];

  const recoveryBenefits = [
    { icon: <CheckCircle className="w-6 h-6" />, text: "Break negative patterns" },
    { icon: <TrendingUp className="w-6 h-6" />, text: "Sustained motivation" },
    { icon: <Shield className="w-6 h-6" />, text: "Addiction recovery" },
    { icon: <Heart className="w-6 h-6" />, text: "Emotional healing" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-orange-50 to-green-50">
      {/* Navigation Bar */}
      <nav className="bg-white/90 backdrop-blur-sm border-b sticky top-0 z-50">
        <div className="container mx-auto max-w-6xl px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <RefreshCw className="w-8 h-8 text-primary" />
              <span className="text-xl font-bold text-foreground">Dislocation Therapy Guide</span>
            </div>
            <div className="flex items-center gap-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.open('https://www.keystonetherapy.com.au', '_blank')}
                className="flex items-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 px-4">
        <div className="absolute inset-0 bg-[var(--gradient-healing)] opacity-10"></div>
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <RefreshCw className="w-20 h-20 text-primary animate-spin" style={{animationDuration: '3s'}} />
                <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse"></div>
              </div>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
              Dislocation Therapy
            </h1>
            <h2 className="text-3xl md:text-4xl font-semibold mb-6 text-secondary">
              Motivation & Addiction Recovery
            </h2>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Transform your life by disrupting negative patterns, building sustainable motivation, and overcoming addictive behaviors through evidence-based therapeutic approaches.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {recoveryBenefits.map((benefit, index) => (
                <Badge key={index} variant="secondary" className="px-4 py-2 text-sm">
                  <span className="mr-2 text-primary">{benefit.icon}</span>
                  {benefit.text}
                </Badge>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-white px-8 py-3 text-lg shadow-lg hover:shadow-xl transition-all duration-300">
                Begin Recovery
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-primary text-primary hover:bg-primary hover:text-white px-8 py-3 text-lg"
                onClick={() => window.open('https://www.keystonetherapy.com.au', '_blank')}
              >
                Get Professional Support
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Dislocation Therapy Section */}
      <section className="py-16 px-4 bg-white/80 backdrop-blur-sm">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-foreground">What is Dislocation Therapy?</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Dislocation therapy is a powerful approach that disrupts negative behavioral patterns and creates space for positive change. 
              By strategically interrupting harmful cycles, we can rebuild healthier habits and thought patterns.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1620148222862-b95cf7405a7b?w=600&auto=format&fit=crop&q=80" 
                alt="Therapy and healing process" 
                className="rounded-2xl shadow-2xl w-full h-80 object-cover"
              />
            </div>
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-6 rounded-xl">
                <h3 className="text-2xl font-semibold mb-3 text-primary">Core Principles</h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <RefreshCw className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                    <span>Disrupt negative patterns before they solidify</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Target className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                    <span>Create intentional space for new behaviors</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                    <span>Build protective factors against relapse</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <TrendingUp className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                    <span>Sustainable motivation through intrinsic rewards</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Therapeutic Approaches Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-foreground">Comprehensive Recovery Approaches</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Integrate these evidence-based therapeutic methods to break negative patterns, build motivation, and achieve lasting recovery from addictive behaviors.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {therapyApproaches.map((approach, index) => (
              <Card key={index} className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                <div className="relative overflow-hidden rounded-t-lg">
                  <img 
                    src={approach.image} 
                    alt={approach.title}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-lg">
                    <span className="text-primary">{approach.icon}</span>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl text-foreground group-hover:text-primary transition-colors">
                    {approach.title}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground">
                    {approach.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm text-primary mb-3">Key Techniques:</h4>
                    <ul className="space-y-2">
                      {approach.techniques.map((technique, techIndex) => (
                        <li key={techIndex} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <div className="w-1.5 h-1.5 bg-accent rounded-full flex-shrink-0"></div>
                          {technique}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Professional Disclaimer */}
      <section className="py-12 px-4 bg-amber-50 border-l-4 border-amber-400">
        <div className="container mx-auto max-w-4xl">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-8 h-8 text-amber-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-xl font-semibold mb-3 text-amber-800">Important Professional Disclaimer</h3>
              <p className="text-amber-700 mb-4">
                This website provides educational information about therapeutic approaches and is not a substitute for professional medical or psychological treatment. 
                If you're struggling with addiction, mental health issues, or need immediate support, please consult with qualified healthcare professionals.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  variant="outline" 
                  className="border-amber-600 text-amber-700 hover:bg-amber-100"
                  onClick={() => window.open('https://www.keystonetherapy.com.au', '_blank')}
                >
                  Find Professional Help
                </Button>
                <Button variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-100">
                  Crisis Resources
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Support CTA */}
      <section className="py-12 px-4 bg-gradient-to-r from-primary/10 to-secondary/10">
        <div className="container mx-auto max-w-4xl text-center">
          <h3 className="text-2xl font-bold mb-4 text-foreground">Need Professional Therapy Support?</h3>
          <p className="text-lg text-muted-foreground mb-6">
            While these techniques provide valuable insights, professional therapy is essential for addressing addiction, trauma, and complex behavioral patterns. Get personalized support from qualified therapists.
          </p>
          <Button 
            size="lg" 
            className="bg-primary hover:bg-primary/90 text-white px-8 py-3 text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 mx-auto"
            onClick={() => window.open('https://www.keystonetherapy.com.au', '_blank')}
          >
            <ExternalLink className="w-5 h-5" />
            Contact Keystone Therapy
          </Button>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 px-4 bg-gradient-to-r from-primary via-secondary to-accent">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">
            Begin Your Recovery Journey Today
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Breaking negative patterns and building sustainable motivation is possible. Take the first step toward lasting change and recovery.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-white/90 px-8 py-3 text-lg shadow-lg">
              Get Started
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-primary px-8 py-3 text-lg flex items-center gap-2"
              onClick={() => window.open('https://www.keystonetherapy.com.au', '_blank')}
            >
              <ExternalLink className="w-4 h-4" />
              Contact Support
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-white/90 backdrop-blur-sm border-t">
        <div className="container mx-auto max-w-6xl text-center">
          <p className="text-muted-foreground mb-2">
            © 2024 Dislocation Therapy Guide. Supporting recovery through evidence-based therapeutic approaches.
          </p>
          <p className="text-sm text-muted-foreground">
            For educational purposes only. Always consult qualified professionals for treatment.
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            For professional therapy and support, visit{" "}
            <a 
              href="https://www.keystonetherapy.com.au" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline font-medium"
            >
              Keystone Therapy
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;